import { formatarDinheiro, formatarDataCurta } from '../utils';
import type { SaldoInscricao, ItemFinanceiro, MetodoPagamento } from '../types';

const METODO_LABEL: Record<MetodoPagamento, string> = {
  pix: 'Pix',
  dinheiro: 'Dinheiro',
  transferencia: 'Transferência',
  cartao: 'Cartão',
  outro: 'Outro',
};

interface Props {
  saldo: SaldoInscricao;
  itens: ItemFinanceiro[];
  aoRegistrarPagamento: () => void;
  aoConcederAjuste: () => void;
  aoEstornar: (id: number) => void;
}

function Bloco({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div className="min-w-0 rounded-2xl bg-background-50 p-4">
      <span
        className={`block truncate text-[15px] font-bold leading-tight ${
          cor ?? 'text-foreground-950'
        }`}
      >
        {valor}
      </span>
      <span className="mt-1 block truncate text-[13px] text-foreground-500">{rotulo}</span>
    </div>
  );
}

function Linha({
  item,
  aoEstornar,
}: {
  item: ItemFinanceiro;
  aoEstornar: (id: number) => void;
}) {
  if (item.tipo === 'ajuste') {
    const verbo = item.ajuste_tipo === 'isencao' ? 'Isenção concedida' : 'Desconto concedido';
    return (
      <li className="py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[15px] font-medium text-primary-700">
              {verbo}
              {item.nome ? ` por ${item.nome}` : ''}
            </p>
            <p className="mt-0.5 text-[13px] text-foreground-500">{formatarDataCurta(item.data)}</p>
            {item.motivo_interno && (
              <p className="mt-0.5 text-[13px] text-foreground-500">{item.motivo_interno}</p>
            )}
          </div>
          <span className="whitespace-nowrap text-[15px] font-medium text-primary-700">
            {formatarDinheiro(-item.valor_centavos)}
          </span>
        </div>
      </li>
    );
  }

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] text-foreground-900">
            {item.metodo ? METODO_LABEL[item.metodo] : 'Pagamento'}
          </p>
          <p className="mt-0.5 text-[13px] text-foreground-500">
            {formatarDataCurta(item.data)}
            {item.nome ? ` · por ${item.nome}` : ''}
          </p>
          {item.motivo_interno && (
            <p className="mt-0.5 text-[13px] text-foreground-500">{item.motivo_interno}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="whitespace-nowrap text-[15px] font-medium text-accent-700">
            {formatarDinheiro(item.valor_centavos)}
          </span>
          <button
            type="button"
            onClick={() => aoEstornar(item.id)}
            className="cursor-pointer whitespace-nowrap text-[13px] font-medium text-danger-600 transition-colors hover:text-danger-700"
          >
            Estornar
          </button>
        </div>
      </div>
    </li>
  );
}

export default function AbaFinanceiro({
  saldo,
  itens,
  aoRegistrarPagamento,
  aoConcederAjuste,
  aoEstornar,
}: Props) {
  const total =
    saldo.contratado_centavos + saldo.acrescimos_centavos - saldo.descontos_isencoes_centavos;
  const falta = Math.max(0, saldo.saldo_centavos);
  const faltaCor = saldo.saldo_centavos <= 0 ? 'text-accent-700' : 'text-amber-700';
  const quitado = saldo.saldo_centavos <= 0;

  return (
    <div>
      <div className="grid grid-cols-3 gap-3">
        <Bloco rotulo="Valor" valor={formatarDinheiro(total)} />
        <Bloco rotulo="Já recebido" valor={formatarDinheiro(saldo.pago_centavos)} />
        <Bloco rotulo="Falta" valor={formatarDinheiro(falta)} cor={faltaCor} />
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={aoRegistrarPagamento}
          disabled={quitado}
          className="flex min-h-[52px] cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 px-4 text-[15px] font-bold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Registrar pagamento
        </button>
        <button
          type="button"
          onClick={aoConcederAjuste}
          className="flex min-h-[52px] cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl border border-primary-200 bg-white px-4 text-[15px] font-bold text-primary-700 transition-colors hover:bg-primary-50"
        >
          Dar desconto
        </button>
      </div>

      <div className="mt-5 border-t border-background-100 pt-2">
        {itens.length === 0 ? (
          <p className="py-4 text-[15px] text-foreground-500">Nada registrado ainda.</p>
        ) : (
          <ul className="divide-y divide-background-100">
            {itens.map((item) => (
              <Linha key={item.chave} item={item} aoEstornar={aoEstornar} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}